import { CircularProgress, IconButton, Stack, TextField, Typography } from '@mui/material'
import React, { useState, useRef, useEffect } from 'react'
import { apiDelete, apiGet, apiPost, apiPut } from '../../generic/Api_Functions'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import AddElementPart from '../../generic/smartAddElement/AddElementPart'
import { ManageSearchOutlined, SquareFoot, TextSnippetOutlined, Tune } from '@mui/icons-material'
import Part from './Part'
import PartReferencePreset from './PartReferencePreset'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import Document from '../document/Document'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import FeatureResourceAssignment from './FeatureResourceAssignment'
import AlertUI from '../../generic/AlertUI'
import useGetExtendedColumns from '../../generic/hooks/useGetExtendedColumns/useGetExtendedColumns'
import CircularProgressWithLabel from '../../generic/CircularProgressWithLabel'
import useFrontEndConfig from '../../generic/hooks/useFrontEndConfig/useFrontEndConfig'
import MainImageThumbnail, { COLUMN_WIDTH as MAIN_IMAGE_COLUMN_WIDTH } from './MainImageThumbnail'
import useMainImageResolution from './useMainImageResolution'
import useMinimumStockStatus from './useMinimumStockStatus'
import MinimumStockIndicator from './MinimumStockIndicator'
import useRepositorySelection from '../../generic/hooks/useRepositorySelection'
import { parentPreselectedRecordFrom, childPreselectedRecordFrom, childPreselectedRecordForRow } from '../../generic/utilities/repositorySelectionMapping'
import SensitiveBoundary from '../../vision/SensitiveBoundary'
import { useProtectedHidden } from '../../vision/visionContext'
import { useProtectedRowPlaceholder, useVisionCoversSensitive } from '../../vision/protectedRow'
const Reference = () => {
  const parallelepiped = 'PARALLELEPIPED'
  const cylindrical = 'CYLINDRICAL'
  const createPartEndPoint = 'parts'
  const [showPartReference, setShowPartReference] = useState(false)
  const isUserAuthorized = useAuthorization()
  // Historia 170 (J4b, S89/S103): PART_REFERENCE -> (child) PART. `onPreselectionResolved` opens the
  // "View Parts" dialog automatically ONLY when there is a child selection to hand it (S89's own
  // `null`-selection example never opens it — the row is preselected in THIS table and nothing more).
  const repositorySelection = useRepositorySelection()
  const handlePartReferencePreselectionResolved = (row) => {
    if (row && childPreselectedRecordFrom(repositorySelection)) {
      setSelectedRow(row)
      setShowPartReference(true)
    }
  }
  const [showFeatures, setShowFeatures] = useState(false)
  const [showPreset, setShowPreset] = useState(false)
  const [showDocument, setShowDocument] = useState(false)
  const [showClusterDownload, setShowClusterDownload] = useState(false)
  const [editing, setEditing] = useState(false)
  const disableRowActions = useRef(false)
  const [quantityValue, setQuantityValue] = useState(0)
  const [selectedRow, setSelectedRow] = useState({})
  const [currentEditingRow, setCurrentEditingRow] = useState(null)
  const [creating, setCreating] = useState(false)
  const { t_ } = useSmartTranslation()
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const [AlertElementDownloadDialog, showAlertDownloadDialog] = AlertUI()
  const [isPartReferenceDownloading, setIsPartReferenceDownloading] = useState(false)
  const [partReferenceDownloadProgress, setPartReferenceDownloadProgress] = useState(null)
  const [mainImageInfoMap, resolveMainImages, mergeMainImages, mainImageThumbnailMap] = useMainImageResolution()
  // Story 213 S243 (H-R2-1): while the vigilance covers, the thumbnails of protected rows are not asked for; the ones the
  // cover left out are asked for once it uncovers. The page is fetched by the table, so the cover is read from a ref.
  const protectedHidden = useProtectedHidden()
  // Story 213 S302 (C15.6): while it covers, the whole row of a protected product is the blurred placeholder.
  const protectedRowPlaceholder = useProtectedRowPlaceholder()
  const visionCoversSensitive = useVisionCoversSensitive()
  const coveredRef = useRef(false)
  coveredRef.current = protectedHidden
  const thumbnailsLeftOutRef = useRef([])
  useEffect(() => {
    if (!protectedHidden && thumbnailsLeftOutRef.current.length > 0) {
      resolveMainImages(thumbnailsLeftOutRef.current)
      thumbnailsLeftOutRef.current = []
    }
  }, [protectedHidden, resolveMainImages])
  const [minimumStockStatusMap, resolveMinimumStockStatuses] = useMinimumStockStatus()
  const extendedColumns = useGetExtendedColumns('part_reference')

  const frontEndConfig = useFrontEndConfig()[0]
  useEffect(() => {
    let mounted = true
    const interval = setInterval(async () => {
      const response = await apiGet('reference/downloading')

      if (mounted) {
        setShowClusterDownload(response.working)
        setPartReferenceDownloadProgress(response.progress)
        if (isPartReferenceDownloading !== response.working) {
          setIsPartReferenceDownloading(response.working)
        }
      }
    }, 1000)
    return () => {
      mounted = false
      clearInterval(interval)
    }
  }, [isPartReferenceDownloading])

  const mandatoryCondition = (row, property) => {
    if (!row.shape) return false
    if (row.shape.code === property) return true
    return false
  }

  const dataFetch = async (pagination) => {
    const data = await apiGet('partReferencesPagination', pagination)
    // Resolve the main-image metadata for the whole visible page with a single batch call
    // (anti-N+1). The result feeds the thumbnail column; never one request per row.
    const rows = Array.isArray(data) ? data : (data?.content || [])
    const leftOut = (row) => coveredRef.current && row.sensitive === true
    thumbnailsLeftOutRef.current = rows.filter(leftOut).map((row) => row.id)
    resolveMainImages(rows.filter((row) => !leftOut(row)).map((row) => row.id))
    // S17 (CU4): resolve the minimum-stock alert status for the whole visible page with a
    // single batch call (anti-N+1), same pattern as resolveMainImages above.
    resolveMinimumStockStatuses(rows.map((row) => row.id))
    return data
  }

  const checkDimensionCondition = (row, column) => {
    let errorMsg = ''
    if ((row.rawMaterialReference !== null && row.rawMaterialReference !== undefined && row.rawMaterialReference !== '') && (Object.keys(row.rawMaterialReference).length > 0) &&
    (row.shape && Object.keys(row.shape).length > 0) && row.rawMaterialReference.shape) {
      if (row.shape.code === row.rawMaterialReference.shape.code) {
        if (row.shape.code === cylindrical) {
          if (row.diameter > row.rawMaterialReference.diameter || row.length > row.rawMaterialReference.length) {
            errorMsg = 'Part reference dimensions must be smaller than those of the raw material reference'
          }
        } else if (row.shape.code === parallelepiped) {
          if (row.lengthX > row.rawMaterialReference.lengthX || row.lengthY > row.rawMaterialReference.lengthY || row.lengthZ > row.rawMaterialReference.lengthZ) {
            errorMsg = 'Part reference dimensions must be smaller than those of the raw material reference'
          }
        }
      } else {
        errorMsg = 'Part reference shape and raw material reference shape must be equals'
      }
    }
    return errorMsg
  }

  const handleAlert = (extraFunctions) => {
    return extraFunctions.showAlert
  }
  const readOnlyCondition = (row, property) => {
    if (
      !row.shape ||
      row.shape.code !== property ||
      row.shape.code === '' ||
      row.shape.code === undefined
    ) { return true }
    return false
  }
  const checkMinimumBatchCondition = (row) => {
    let errorMsg = ''
    if (row.minimumManufacturingBatch < 1) {
      errorMsg = t_('Minimum manufacturing batch must be greater than or equal to 1')
    }
    return errorMsg
  }
  // Story 45 (CU1/CA1). V2 (as amended — D5 amendment of 2026-07-13): a controlled reference must
  // have an informed threshold >= 1; 0 is no longer a valid minimum when controlled. The backend
  // enforces the same rule in the domain (MinimumStockPolicy, E6/E7) for every channel (API/CSV).
  // Typing "e" in the numeric input (HTML scientific notation) yields an empty value, which lands
  // on one of these rules instead of silently reaching the backend as null.
  const checkMinimumStockCondition = (row) => {
    const value = row.minimumStock
    const isEmpty = value === undefined || value === null || value === ''
    if (row.minimumStockControlled) {
      if (isEmpty) {
        return t_('Minimum stock is required when minimum stock controlled is enabled')
      }
      if (!Number.isInteger(Number(value)) || Number(value) < 1) {
        return t_('Minimum stock must be greater than or equal to 1 when minimum stock controlled is enabled')
      }
      return ''
    }
    if (!isEmpty && (!Number.isInteger(Number(value)) || Number(value) < 0)) {
      return t_('Minimum stock must be an integer greater than or equal to 0')
    }
    return ''
  }

  const columns = [
    {
      name: t_('Main image'),
      field: 'mainImage',
      width: MAIN_IMAGE_COLUMN_WIDTH,
      readOnly: true,
      disableFilter: true,
      render: (row) => {
        if (row === undefined || row.id === undefined) {
          return null
        }
        // S243: the image is the product's content; covered, a protected one is the small blurred placeholder (C15.6).
        return (
          <SensitiveBoundary sensitive={row.sensitive === true} compact>
            <MainImageThumbnail
              partReferenceId={row.id}
              mainImageInfo={mainImageInfoMap[row.id] || {}}
              thumbnail={mainImageThumbnailMap[row.id] || ''}
            />
          </SensitiveBoundary>
        )
      }
    },
    {
      name: t_('Code'),
      field: 'code',
      readOnly: true,
      charLimit: 60,
      mandatory: true,
      unique: true
    },
    {
      name: t_('Name'),
      field: 'name',
      charLimit: 80,
      mandatory: true
    },
    {
      name: t_('Description'),
      field: 'description',
      charLimit: 200
    },
    {
      name: t_('Material type'),
      field: 'materialType.name',
      type: 'smartSelect',
      edit: {
        source: async () => {
          return await apiGet('materialTypes')
        },
        id: 'id',
        field: 'name'
      },
      mandatory: true
    },
    {
      name: t_('Raw Material Reference'),
      field: 'rawMaterialReference.code',
      mandatory: quantityValue > 0 ? true : undefined,
      type: 'smartSelect',
      edit: {
        source: async () => {
          return await apiGet('rawMaterialsReference')
        },
        id: 'id',
        field: 'code'
      },
      condition: (rowData, column) => {
        return checkDimensionCondition(rowData, column)
      }
    },
    {
      name: t_('Shape'),
      field: 'shape.name',
      type: 'smartSelect',
      edit: {
        source: async () => {
          return await apiGet('shapes')
        },
        id: 'id',
        field: 'name'
      },
      condition: (rowData, column) => {
        return checkDimensionCondition(rowData, column)
      }
    },
    {
      name: t_('X Length (mm)'),
      field: 'lengthX',
      showWhenColumnVisible: 'shape.name',
      readOnly: (rowData) => {
        return readOnlyCondition(rowData, parallelepiped)
      },
      mandatory: (rowData) => {
        return mandatoryCondition(rowData, parallelepiped)
      },
      reset: (rowData) => {
        return readOnlyCondition(rowData, parallelepiped)
      },
      condition: (rowData, column) => {
        return checkDimensionCondition(rowData, column)
      },
      numeric: true
    },
    {
      name: t_('Y Length (mm)'),
      field: 'lengthY',
      showWhenColumnVisible: 'shape.name',
      readOnly: (rowData) => {
        return readOnlyCondition(rowData, parallelepiped)
      },
      mandatory: (rowData) => {
        return mandatoryCondition(rowData, parallelepiped)
      },
      reset: (rowData) => {
        return readOnlyCondition(rowData, parallelepiped)
      },
      condition: (rowData, column) => {
        return checkDimensionCondition(rowData, column)
      },
      numeric: true
    },

    {
      name: t_('Z Length (mm)'),
      field: 'lengthZ',
      showWhenColumnVisible: 'shape.name',
      readOnly: (rowData) => {
        return readOnlyCondition(rowData, parallelepiped)
      },
      mandatory: (rowData) => {
        return mandatoryCondition(rowData, parallelepiped)
      },
      reset: (rowData) => {
        return readOnlyCondition(rowData, parallelepiped)
      },
      condition: (rowData, column) => {
        return checkDimensionCondition(rowData, column)
      },
      numeric: true
    },

    {
      name: t_('Diam (mm)'),
      field: 'diameter',
      showWhenColumnVisible: 'shape.name',
      readOnly: (rowData) => {
        return readOnlyCondition(rowData, cylindrical)
      },
      mandatory: (rowData) => {
        return mandatoryCondition(rowData, cylindrical)
      },
      reset: (rowData) => {
        return readOnlyCondition(rowData, cylindrical)
      },
      condition: (rowData, column) => {
        return checkDimensionCondition(rowData, column)
      },
      numeric: true
    },

    {
      name: t_('Length (mm)'),
      field: 'length',
      showWhenColumnVisible: 'shape.name',
      readOnly: (rowData) => {
        return readOnlyCondition(rowData, cylindrical)
      },
      mandatory: (rowData) => {
        return mandatoryCondition(rowData, cylindrical)
      },
      reset: (rowData) => {
        return readOnlyCondition(rowData, cylindrical)
      },
      condition: (rowData, column) => {
        return checkDimensionCondition(rowData, column)
      },
      numeric: {
        default: 0
      }
    },
    {
      name: t_('Fixture'),
      field: 'defaultFixtureReference.code',
      type: 'smartSelect',
      edit: {
        source: async () => {
          if (currentEditingRow !== null) {
            return await apiGet('fixtureReferencesByPartReference/' + currentEditingRow.id)
          }
          if (creating) {
            return await apiGet('fixtureReferencesByPartReference/' + 0)
          }
        },
        id: 'id',
        field: 'code'
      }
    },
    {
      name: t_('Client code'),
      field: 'client.code',
      autoSelectFirst: false,
      type: 'smartSelect',
      edit: {
        source: async () => {
          return await apiGet('clientsBasicData')
        },
        id: 'id',
        field: 'code'
      }
    },
    {
      name: t_('Client name'),
      field: 'client.name',
      type: 'smartSelect',
      autoSelectFirst: false,
      disabled: true
    },
    {
      name: t_('Quantity'),
      field: 'quantity',
      numeric: true,
      defaultNewValue: 0,
      mandatory: true,
      disableFilter: true,
      render: (row, extraFunctions) => {
        return (
          row !== undefined && row.id === undefined && row.quantity === 0
            ? (
              <TextField
                value={quantityValue}
                onChange={(event) => {
                  setQuantityValue(event.target.value)
                }}
                helperText={' '}
                type='number'
                disabled={!isUserAuthorized('ADD_PART_INSTANCE')}
                inputProps={{ min: 0 }}
              />)
            : (
              <div id={'quantity' + row.id} style={{ alignItems: 'center', display: 'flex' }}>
                {row.quantity}
                <AddElementPart
                  endpoint={createPartEndPoint}
                  partReference={row}
                  referenceName={row.name}
                  row={row}
                  disable={editing || disableRowActions.current || !isUserAuthorized('ADD_PART_INSTANCE')}
                  onAlert={handleAlert(extraFunctions)}
                />
              </div>))
      }
    },
    {
      name: t_('Minimum manufacturing batch'),
      field: 'minimumManufacturingBatch',
      numeric: true,
      defaultNewValue: 1,
      mandatory: true,
      condition: (rowData) => {
        return checkMinimumBatchCondition(rowData)
      }
    },
    {
      // S6 (CU1/CA1): flag that opts the reference into minimum-stock monitoring. Independent
      // of `minimumStock` (RN2): disabling it keeps the threshold, it is not cleared here.
      name: t_('Minimum stock controlled'),
      field: 'minimumStockControlled',
      type: 'boolean',
      defaultNewValue: false
    },
    {
      // S6 (CU1/CA1): the threshold used to detect the minimum-stock breach (RN4). Both fields
      // travel to the backend as part of the row payload via the existing
      // apiPut('partReferences', row) / apiPost('partReferences', row) calls.
      // V1/V2 validated on save via `condition` (same pattern as minimumManufacturingBatch).
      // V2: `mandatory` is row-dependent (the generic table supports a function) — the field is
      // required exactly while `minimumStockControlled` is checked, optional otherwise (RN2).
      name: t_('Minimum stock'),
      field: 'minimumStock',
      numeric: true,
      defaultNewValue: 0,
      mandatory: (row) => Boolean(row && row.minimumStockControlled),
      condition: (rowData) => {
        return checkMinimumStockCondition(rowData)
      }
    },
    {
      // S17/S18 (CU4/CA4/CA6): stock contable vs. mínimo + alert indicator. Read-only: the value
      // is derived (RN3/RN4), never edited directly from this table.
      name: t_('Minimum stock alert'),
      field: 'minimumStockAlert',
      readOnly: true,
      disableFilter: true,
      render: (row) => {
        if (row === undefined || row.id === undefined) {
          return null
        }
        return (
          <MinimumStockIndicator status={minimumStockStatusMap[row.id]} />
        )
      }
    },
    {
      name: t_('Remark'),
      field: 'comment',
      charLimit: 255
    },
    {
      name: t_('Creation date'),
      field: 'createDate',
      type: 'datetime',
      readOnly: () => true
    },
    {
      name: t_('Last update date'),
      field: 'updateDate',
      type: 'datetime',
      readOnly: () => true
    },
    {
      name: t_('Creation node'),
      field: 'createNode',
      readOnly: () => true
    },
    {
      name: t_('Last update node'),
      field: 'updateNode',
      readOnly: () => true
    },
    {
      name: t_('User'),
      field: 'user',
      readOnly: () => true
    }
  ]

  const getColumns = () => {
    if (extendedColumns?.length === 0) {
      return columns
    }
    return columns.concat(extendedColumns)
  }

  // Story 45 (V2, D5 amendment). Pre-save validation, same behavior as quantity/order in
  // FabricationOrder.jsx: the table's save button runs `validator` (validateColumError) BEFORE
  // calling the fetchFunction, marks the offending field in error and blocks the save entirely —
  // no API round-trip. The column `condition` alone was not enough: it is only re-evaluated when
  // the column's own value changes, so checking the flag while leaving the default 0 untouched
  // reached the backend and bounced with a raw MINIMUM_STOCK_THRESHOLD_NOT_POSITIVE.
  const validator = (row) => {
    const errorMsg = checkMinimumStockCondition(row)
    if (errorMsg !== '') {
      return { errorFields: ['minimumStock'], alertMessage: errorMsg }
    }
    return null
  }

  // Defense in depth for the same rule at the save entry point (kept in case any save path skips
  // the table validator); the table surfaces the rejection through its standard error alert.
  const rejectIfMinimumStockInvalid = (row) => {
    const errorMsg = checkMinimumStockCondition(row)
    if (errorMsg !== '') {
      const validationError = new Error(errorMsg)
      // The table's save handler uses error.name as the alert severity (MUI expects lowercase).
      validationError.name = 'error'
      return Promise.reject(validationError)
    }
    return null
  }

  const actions = {
    edit: {
      fetchFunction: (row) => {
        const invalid = rejectIfMinimumStockInvalid(row)
        if (invalid) {
          return invalid
        }
        return apiPut('partReferences', row)
      },
      disabled: !isUserAuthorized('EDIT_PART_REFERENCE')
    },
    create: {
      fetchFunction: async (row) => {
        const invalid = rejectIfMinimumStockInvalid(row)
        if (invalid) {
          return invalid
        }
        row.quantity = quantityValue
        // Check if reference exists on digital repository
        let exists
        try {
          exists = await apiGet('partReference/exists/byCode?code=' + row.code)
        } catch (e) {
          showAlertDownloadDialog({
            title: e.message,
            severity: 'warning'
          })
          exists = false
        }
        if (exists) {
          const response = await apiGet('getRequestedPartReference?partReferenceCode=' + row.code)
          await apiPost('downloadReference', response)
            .catch((e) => {
              showAlertDownloadDialog({
                title: e.message,
                severity: 'error'
              })
            })
          return
        }

        return apiPost('partReferences', row)
      },
      disabled: !isUserAuthorized('ADD_PART_REFERENCE')
    },
    delete: {
      fetchFunction: (rows) => {
        if (rows.length === 1) {
          return apiDelete('partReferences/' + rows[0].id, rows)
        } else {
          return apiDelete('partReferencesMulti', rows)
        }
      },
      disabled: !isUserAuthorized('DELETE_PART_REFERENCE')
    },
    resync: {
      resource: 'PART_REFERENCE',
      disabled: !isUserAuthorized('PART_RESYNC'),
      visibilityButton: (row) => { return frontEndConfig.inCluster }
    },
    custom: [
      {
        name: 'Documents',
        render: (row) => {
          return (
            <IconButton>
              <TextSnippetOutlined />
            </IconButton>
          )
        },
        disabled: editing || !isUserAuthorized('DOCUMENTS_PART_REFERENCE'),
        onClick: (row) => {
          setSelectedRow(row)
          setShowDocument(true)
        }
      },
      {
        name: 'View Parts',
        render: (row) => {
          return (
            <IconButton>
              <ManageSearchOutlined />
            </IconButton>
          )
        },
        disabled: !!editing || !isUserAuthorized('PART_INSTANCES'),
        onClick: (row) => {
          setSelectedRow(row)
          setShowPartReference(true)
        }
      },
      {
        name: 'Features',
        render: (row) => {
          return (
            <IconButton>
              <SquareFoot />
            </IconButton>
          )
        },
        onClick: (row) => {
          setSelectedRow(row)
          setShowFeatures(true)
        },
        disabled: true
      },
      {
        name: 'Preset',
        render: (row) => {
          return (
            <IconButton>
              <Tune />
            </IconButton>
          )
        },
        disabled: !!editing || !isUserAuthorized('FEATURE_PART_REFERENCE_PRESET'),
        onClick: (row) => {
          setSelectedRow(row)
          setShowPreset(true)
        }
      }
    ]
  }

  const onEditIndexChanged = (editIndex, row) => {
    setCreating(false)
    if (editIndex === -1) {
      // Creating new element
      setCreating(true)
    } else if (editIndex >= 0) {
      // Editing element. Row will have data
      setCurrentEditingRow(row)
    } else {
      setCurrentEditingRow(null)
    }
  }

  if (extendedColumns === null || extendedColumns === undefined) {
    return <CircularProgress />
  }

  return (
    <div>
      <SmartDialog
        closeCallback={() => {
          setShowDocument(false)
          // The main image may have been (un)marked in the document manager; re-resolve this
          // reference so its thumbnail reflects the change without a full page reload.
          mergeMainImages([selectedRow?.id])
        }}
        accept={false}
        cancel={false}
        close
        disableButtons={isDownloadingFile}
        renderComponent={<SensitiveBoundary sensitive={selectedRow.sensitive === true}><Document reference={selectedRow} resourceType='PART_REFERENCE' isDownloadingFile={isDownloadingFile} setIsDownloadingFile={setIsDownloadingFile} /></SensitiveBoundary>}
        setOpen={showDocument}
      />
      <SmartDialog
        closeCallback={() => setShowClusterDownload(false)}
        accept={false}
        cancel={false}
        close
        renderComponent={
          <Stack sx={{ padding: '1rem' }} gap={5}>
            <Typography variant='h3'>{t_('Downloading reference')}</Typography>
            {!isPartReferenceDownloading ? AlertElementDownloadDialog : null}
            {isPartReferenceDownloading ? <Typography>{t_('The reference exists in the digital repository. Information about the reference is being downloaded.')}</Typography> : null}
            {isPartReferenceDownloading ? <div><CircularProgressWithLabel value={partReferenceDownloadProgress} /></div> : null}
          </Stack>
}
        setOpen={showClusterDownload}
      />
      <SmartDialog
        closeCallback={() => setShowFeatures(false)}
        accept={false}
        cancel={false}
        close
        renderComponent={<SensitiveBoundary sensitive={selectedRow.sensitive === true}><FeatureResourceAssignment resource={selectedRow} resourceType='PART_REFERENCE' resourceTypeName='part reference' /></SensitiveBoundary>}
        setOpen={showFeatures}
      />
      <SmartDialog
        closeCallback={() => setShowPreset(false)}
        accept={false}
        cancel={false}
        close
        title={{ render: <Typography variant='h5'>{t_('Preset') + ': ' + (selectedRow.name || '')}</Typography> }}
        renderComponent={<SensitiveBoundary sensitive={selectedRow.sensitive === true}><PartReferencePreset partReferenceId={selectedRow.id} /></SensitiveBoundary>}
        setOpen={showPreset}
      />
      <SmartDialog
        renderComponent={<SensitiveBoundary sensitive={selectedRow.sensitive === true}><Part partReference={selectedRow} showQrAction preselectedRecord={childPreselectedRecordForRow(repositorySelection, selectedRow.id)} /></SensitiveBoundary>}
        setOpen={showPartReference}
        cancel={false}
        accept={false}
        close
        closeCallback={() => { setShowPartReference(false) }}
      />
      <SmartMateriaUITable
        tableTuneUniqueKey='partReference'
        rowPlaceholder={protectedRowPlaceholder}
        disableSelectAllData={visionCoversSensitive}
        pageable
        columns={getColumns()}
        title={t_('Parts')}
        dataFetch={dataFetch}
        actions={actions}
        validator={validator}
        preselectedRecord={parentPreselectedRecordFrom(repositorySelection)}
        onPreselectionResolved={handlePartReferencePreselectionResolved}
        multipleSelection
        editingRow={(event) => setEditing(event)}
        onEditIndexChanged={onEditIndexChanged}
        setDisableRowActions={(event) => disableRowActions.current === event}
        importFile={[{
          bean: {
            beanDtoName: 'ImportPartReference',
            beanDtoPackage: 'com.smartpm.sun.dto',
            beanName: 'PartReferenceEntity',
            beanPackage: 'com.smartpm.sun.usecase.partreference.infrastructure.postgres',
            servicePackage: 'com.smartpm.sun.dbservices',
            serviceName: 'PartReferenceService'
          },
          name: t_('Part Reference')
        },
        {
          bean: {
            beanName: 'PartImportDto',
            beanPackage: 'com.smartpm.sun.usecase.partimport.rest',
            serviceName: 'PartImportService',
            servicePackage: 'com.smartpm.sun.usecase.partimport.application'
          },
          name: t_('Part')
        }
        ]}
        importFileDisabled={!isUserAuthorized('IMPORT_FILE_PART_REFERENCE')}
        multipleOptions
      />
    </div>
  )
}

export default Reference
